import { useState, useMemo } from 'react';
import {
  type APIStatusWithAll,
  type SuggestedCandidate,
  API_STATUSES_WITH_ALL,
  API_STATUS_LABELS_WITH_ALL,
} from '../types/SuggestedCandidate';
import useFetchSuggestedCandidates from '../api/useFetchSuggestedCandidates';
import useSuggestCandidates from '../api/useSuggestCandidates';
import SuggestedCandidateListItem from './SuggestedCandidateListItem';
import SuggestCandidatesModal from './SuggestCandidatesModal';
import Checkbox from '@/components/Checkbox';
import { Button } from '@/components/Button';
import { MdAdd } from 'react-icons/md';

type Props = {
  jobPostId: number;
  companyName?: string;
  position?: string;
  location?: string;
};

export default function ProvidedJobPostsSuggestedCandidatesCard({
  jobPostId,
  companyName = '',
  position = '',
  location = '',
}: Props) {
  const [selectedStatuses, setSelectedStatuses] = useState<APIStatusWithAll[]>(['all']);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, error } = useFetchSuggestedCandidates(jobPostId);
  const { mutateAsync: suggestCandidates } = useSuggestCandidates();

  const handleStatusChange = (status: APIStatusWithAll) => {
    if (status === 'all') {
      setSelectedStatuses(['all']);
    } else {
      const newStatuses = selectedStatuses.includes(status)
        ? selectedStatuses.filter((s) => s !== status)
        : [...selectedStatuses.filter((s) => s !== 'all'), status];

      if (newStatuses.length === 0) {
        setSelectedStatuses(['all']);
      } else {
        setSelectedStatuses(newStatuses);
      }
    }
  };

  const filteredCandidates = useMemo(() => {
    if (!data?.suggested_candidates) return [];

    if (selectedStatuses.includes('all')) {
      return data.suggested_candidates;
    }

    return data.suggested_candidates.filter((candidate: SuggestedCandidate) =>
      selectedStatuses.includes(candidate.status)
    );
  }, [data?.suggested_candidates, selectedStatuses]);

  const handleSuggestCandidates = async (candidateProfileIds: number[]) => {
    await suggestCandidates({ jobPostId, candidateProfileIds });
  };

  // 提案済み求職者のIDを抽出
  const suggestedCandidateIds = useMemo(() => {
    if (!data?.suggested_candidates) return [];
    return data.suggested_candidates.map((candidate) => candidate.candidate_profile.id);
  }, [data?.suggested_candidates]);

  return (
    <>
      <div className="relative">
        <div className="h-full">
          <div className="sticky top-[60px] bg-gray">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {API_STATUSES_WITH_ALL.map((status) => (
                  <Checkbox
                    key={status}
                    checked={
                      status === 'all'
                        ? selectedStatuses.includes('all')
                        : selectedStatuses.includes(status) && !selectedStatuses.includes('all')
                    }
                    onChange={() => handleStatusChange(status)}
                  >
                    <span className="text-sm font-normal text-gray-700">
                      {API_STATUS_LABELS_WITH_ALL[status]}
                    </span>
                  </Checkbox>
                ))}
              </div>
              <Button type="button" onClick={() => setIsModalOpen(true)} color="primary">
                <MdAdd className="size-icon-sm" />
                求職者へ提案する
              </Button>
            </div>
            <div className="flex h-[34px] items-center rounded-s bg-tag-gray px-6">
              <div className="grid w-full grid-cols-[2fr_1fr_2fr_1.5fr] gap-4">
                <p className="text-xs font-bold text-sub">求職者</p>
                <p className="text-center text-xs font-bold text-sub">選考ステータス</p>
                <p className="text-center text-xs font-bold text-sub">エージェントからのコメント</p>
                <p className="text-center text-xs font-bold text-sub">アクション</p>
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-1 flex-col gap-3 px-1">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="text-center">
                  <div className="mb-4 inline-block size-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                  <p className="text-gray-500">読み込み中...</p>
                </div>
              </div>
            ) : error ? (
              <div className="py-12 text-center text-red-600">
                エラーが発生しました: {error.message}
              </div>
            ) : filteredCandidates.length === 0 ? (
              <div className="mt-[100px] flex flex-col items-center justify-center gap-6 text-center text-gray-500">
                <p className="text-base font-bold text-main">提案中の求職者がいません</p>
                <img src="/agent/candidates/offer-no-data.svg" width={148} height={144} alt="" />
              </div>
            ) : (
              filteredCandidates.map((candidate) => (
                <SuggestedCandidateListItem
                  key={candidate.id}
                  candidate={candidate}
                  jobPostId={jobPostId}
                />
              ))
            )}
          </div>
        </div>
      </div>
      <SuggestCandidatesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        jobPosts={[
          {
            id: jobPostId,
            companyName,
            position,
            location,
          },
        ]}
        onSubmit={handleSuggestCandidates}
        excludeCandidateIds={suggestedCandidateIds}
      />
    </>
  );
}
